import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, Send, Trash2, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import Natalio from "@/components/Natalio";

const SUGGESTIONS = [
  { icon: "💰", text: "¿Cuánto me deben en total?" },
  { icon: "⚠️", text: "¿Quiénes son mis clientes morosos?" },
  { icon: "📅", text: "¿Qué servicios tengo esta semana?" },
  { icon: "📊", text: "Muéstrame las facturas por facturar" },
];

function ActionCard({ action, onConfirm, onCancel }) {
  const executed = action.status === "executed";
  const cancelled = action.status === "cancelled";
  const busy = action.busy;
  const cls = executed
    ? "border-[#00C853] bg-[#00C853]/10 text-[#008C3A]"
    : cancelled
    ? "border-neutral-300 bg-neutral-100 text-neutral-500"
    : "border-[#FF5A00] bg-[#FF5A00]/5";
  return (
    <div
      className={`mt-2 p-2 border text-xs ${cls}`}
      data-testid={`assistant-action-${action.id}`}
    >
      <div className="font-medium text-neutral-900">{action.preview}</div>
      {!executed && !cancelled && (
        <div className="flex gap-2 mt-1.5">
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid={`assistant-action-confirm-${action.id}`}
            className="bg-[#00C853] hover:bg-[#008C3A] disabled:opacity-50 text-white px-2 py-1 font-mono-kpi text-[10px] uppercase tracking-wider inline-flex items-center gap-1"
          >
            <CheckCircle2 className="w-3 h-3" />{busy ? "Enviando…" : "Confirmar"}
          </button>
          <button
            onClick={onCancel}
            disabled={busy}
            data-testid={`assistant-action-cancel-${action.id}`}
            className="border border-neutral-300 text-neutral-600 hover:bg-neutral-100 disabled:opacity-50 px-2 py-1 font-mono-kpi text-[10px] uppercase tracking-wider"
          >
            Descartar
          </button>
        </div>
      )}
      {executed && (
        <div className="mt-1 font-mono-kpi text-[10px] uppercase">✅ Ejecutado</div>
      )}
      {cancelled && (
        <div className="mt-1 font-mono-kpi text-[10px] uppercase">✖ Descartado</div>
      )}
    </div>
  );
}

export default function AssistantBubble() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("aa_assistant_session") || null);
  const [messages, setMessages] = useState([]); // { role, content, timestamp, actions? }
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const loadedRef = useRef(false);

  // Load session when opened first time.
  useEffect(() => {
    if (!open || !sessionId || loadedRef.current) return;
    loadedRef.current = true;
    api.get(`/assistant/sessions/${sessionId}`).then(({ data }) => {
      // Attach actions to the latest assistant message they belong to.
      // Since we don't track action->message linkage in the DB, we put all actions on the last assistant message.
      const msgs = (data.messages || []).map((m) => ({ ...m }));
      const pending = data.actions || [];
      if (pending.length && msgs.length) {
        for (let i = msgs.length - 1; i >= 0; i--) {
          if (msgs[i].role === "assistant") {
            msgs[i].actions = pending;
            break;
          }
        }
      }
      setMessages(msgs);
    }).catch(() => {
      localStorage.removeItem("aa_assistant_session");
      setSessionId(null);
    });
  }, [open, sessionId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setLoading(true);
    const now = new Date().toISOString();
    setMessages((prev) => [...prev, { role: "user", content: msg, timestamp: now }]);
    try {
      const { data } = await api.post("/assistant/chat", { message: msg, session_id: sessionId });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem("aa_assistant_session", data.session_id);
      }
      const actions = (data.pending_actions || []).map((a) => ({ ...a, status: "pending" }));
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: data.message,
        timestamp: new Date().toISOString(),
        actions: actions.length ? actions : undefined,
      }]);
    } catch (e) {
      toast.error("Natalio falló: " + (formatApiErrorDetail(e.response?.data?.detail) || e.message));
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: "Uf, tuve un problema técnico. ¿Puedes intentarlo de nuevo?",
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId]);

  const updateAction = (actionId, patch) => {
    setMessages((prev) => prev.map((m) => {
      if (!m.actions) return m;
      const idx = m.actions.findIndex((a) => a.id === actionId);
      if (idx === -1) return m;
      const nextActions = m.actions.map((a) => a.id === actionId ? { ...a, ...patch } : a);
      return { ...m, actions: nextActions };
    }));
  };

  const confirmAction = async (actionId) => {
    updateAction(actionId, { busy: true });
    try {
      await api.post(`/assistant/actions/${actionId}/confirm`);
      updateAction(actionId, { status: "executed", busy: false });
      toast.success("Acción ejecutada");
    } catch (e) {
      updateAction(actionId, { busy: false });
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const cancelAction = async (actionId) => {
    updateAction(actionId, { busy: true });
    try {
      await api.post(`/assistant/actions/${actionId}/cancel`);
      updateAction(actionId, { status: "cancelled", busy: false });
    } catch (e) {
      updateAction(actionId, { busy: false });
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const newChat = () => {
    if (messages.length && !window.confirm("¿Iniciar una nueva conversación? La actual quedará guardada.")) return;
    setSessionId(null);
    setMessages([]);
    loadedRef.current = false;
    localStorage.removeItem("aa_assistant_session");
  };

  const hasMessages = messages.length > 0;
  const natalioMood = loading ? "thinking" : hasMessages ? "idle" : "wave";

  return (
    <>
      {!open && (
        <button
          data-testid="assistant-open"
          onClick={() => setOpen(true)}
          className="natalio-bubble fixed bottom-6 right-6 z-40 bg-white hover:bg-neutral-50 border-2 border-[#0A0A0A] rounded-full pl-2 pr-4 py-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.15)] flex items-center gap-2 transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(255,90,0,0.35)]"
          title="Habla con Natalio"
        >
          <Natalio size={40} mood="wave" />
          <span className="font-display font-bold text-sm text-[#0A0A0A]">Natalio</span>
          <span className="natalio-bubble__dot w-2 h-2 rounded-full bg-[#00C853]" />
          <style>{`
            .natalio-bubble__dot {
              box-shadow: 0 0 0 0 rgba(0,200,83,0.6);
              animation: natalio-online 2s infinite;
            }
            @keyframes natalio-online {
              0% { box-shadow: 0 0 0 0 rgba(0,200,83,0.55); }
              70% { box-shadow: 0 0 0 6px rgba(0,200,83,0); }
              100% { box-shadow: 0 0 0 0 rgba(0,200,83,0); }
            }
          `}</style>
        </button>
      )}
      {open && (
        <div
          className="natalio-panel fixed bottom-6 right-6 z-40 w-[380px] max-w-[calc(100vw-1.5rem)] h-[580px] max-h-[calc(100vh-3rem)] bg-white border border-neutral-200 flex flex-col overflow-hidden"
          data-testid="assistant-panel"
        >
          {/* Header */}
          <div className="relative flex items-center justify-between px-4 py-3 bg-gradient-to-br from-[#0A0A0A] via-[#141414] to-[#0A0A0A] text-white">
            <div className="flex items-center gap-3">
              <div className="bg-white/5 border border-white/10 rounded-full p-1">
                <Natalio size={40} mood={natalioMood} />
              </div>
              <div>
                <div className="font-mono-kpi text-[9px] uppercase tracking-[0.2em] text-[#FF5A00]">// Asistente 360</div>
                <div className="font-display font-bold text-sm flex items-center gap-1.5">
                  Natalio
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#00C853]" title="En línea" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={newChat}
                title="Nueva conversación"
                className="p-1.5 hover:bg-white/10 rounded"
                data-testid="assistant-clear"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 hover:bg-white/10 rounded"
                data-testid="assistant-close"
                title="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Accent line */}
            <div className="absolute left-0 right-0 bottom-0 h-[2px] bg-gradient-to-r from-[#FF5A00] via-[#FF8C42] to-[#FF5A00]" />
          </div>
          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-gradient-to-b from-neutral-50 to-white">
            {messages.length === 0 && (
              <div className="text-sm text-neutral-700 space-y-3 pt-2">
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0"><Natalio size={44} mood="wave" /></div>
                  <div>
                    Hola Pablo, soy <b>Natalio</b> 🤖 Puedo consultar tu CRM y proponerte acciones. ¿Empezamos?
                  </div>
                </div>
                <div className="space-y-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s.text}
                      onClick={() => send(s.text)}
                      data-testid={`assistant-suggestion-${s.text.slice(0, 12)}`}
                      className="block w-full text-left px-3 py-2 border border-neutral-200 bg-white hover:border-[#FF5A00] hover:bg-[#FF5A00]/5 text-xs transition-all"
                    >
                      <span className="mr-1.5">{s.icon}</span>{s.text}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`assistant-msg-${i}`}
                className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start items-start"}`}
              >
                {m.role === "assistant" && (
                  <div className="flex-shrink-0 mt-0.5"><Natalio size={28} mood="idle" /></div>
                )}
                <div className={`max-w-[80%] px-3 py-2 text-sm whitespace-pre-wrap rounded-lg ${
                  m.role === "user"
                    ? "bg-[#FF5A00] text-white rounded-tr-none"
                    : "bg-white border border-neutral-200 text-neutral-900 rounded-tl-none shadow-sm"
                }`}>
                  {m.content}
                  {(m.actions || []).map((a) => (
                    <ActionCard
                      key={a.id}
                      action={a}
                      onConfirm={() => confirmAction(a.id)}
                      onCancel={() => cancelAction(a.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-2 justify-start items-start" data-testid="assistant-typing">
                <div className="flex-shrink-0 mt-0.5"><Natalio size={28} mood="thinking" /></div>
                <div className="bg-white border border-neutral-200 rounded-lg rounded-tl-none px-3 py-2 flex gap-1 items-center shadow-sm">
                  <span className="natalio-dot w-1.5 h-1.5 rounded-full bg-neutral-400" />
                  <span className="natalio-dot natalio-dot--2 w-1.5 h-1.5 rounded-full bg-neutral-400" />
                  <span className="natalio-dot natalio-dot--3 w-1.5 h-1.5 rounded-full bg-neutral-400" />
                </div>
              </div>
            )}
          </div>
          {/* Input */}
          <div className="border-t border-neutral-200 p-2.5 bg-white flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Cuéntale a Natalio…"
              disabled={loading}
              className="flex-1 px-3 py-2 border border-neutral-300 focus:border-[#FF5A00] outline-none text-sm disabled:bg-neutral-50 rounded"
              data-testid="assistant-input"
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              data-testid="assistant-send"
              className="bg-[#FF5A00] hover:bg-[#E04A00] disabled:opacity-40 text-white px-3 flex items-center justify-center rounded transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>

          <style>{`
            .natalio-panel {
              border-radius: 16px;
              box-shadow: 0 20px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.04);
              animation: natalio-slide-in 0.24s ease-out;
            }
            @keyframes natalio-slide-in {
              from { opacity: 0; transform: translateY(12px) scale(0.98); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
            .natalio-dot {
              display: inline-block;
              animation: natalio-typing 1.2s ease-in-out infinite;
            }
            .natalio-dot--2 { animation-delay: 0.18s; }
            .natalio-dot--3 { animation-delay: 0.36s; }
            @keyframes natalio-typing {
              0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
              30% { transform: translateY(-4px); opacity: 1; }
            }
          `}</style>
        </div>
      )}
    </>
  );
}
